import { createContext, useContext, useId } from "react";
import { cn } from "@/lib/utils";

/**
 * Label, control, hint and error wired together correctly, once.
 *
 * Forms are where axe finds the most violations, and the ten hand-rolled
 * `rounded-md border border-neutral-300 px-3 py-2` inputs in this codebase had
 * no aria-describedby, no aria-invalid, and announce errors only by turning red.
 * Field owns that plumbing so a caller cannot forget it.
 *
 * The binding travels by context rather than a render prop: a function cannot
 * cross the server/client boundary, so a render-prop API would silently force
 * every page that renders a form to become a client component.
 *
 * The border uses --border-strong, because an input outline is the sole means of
 * identifying the control — WCAG 1.4.11 applies, and the subtle --border
 * (deliberately below 3:1) would fail it.
 */

const controlClasses = [
  "w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)]",
  "px-3 py-2 text-sm text-[var(--text-primary)]",
  "placeholder:text-[var(--text-muted)]",
  "outline-none focus-visible:border-[var(--accent)]",
  "focus-visible:ring-2 focus-visible:ring-[var(--accent-ring)]",
  "disabled:opacity-60",
].join(" ");

type FieldBinding = {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
};

const FieldContext = createContext<FieldBinding | null>(null);

function useFieldBinding(): Partial<FieldBinding> {
  return useContext(FieldContext) ?? {};
}

type FieldProps = {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
};

export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div className={cn("flex flex-col gap-1.5", className)}>
        <label htmlFor={id} className="text-sm font-medium text-[var(--text-primary)]">
          {label}
          {required ? (
            <span className="ml-0.5 text-[var(--danger)]" aria-hidden>
              *
            </span>
          ) : null}
        </label>

        {children}

        {hint ? (
          <p id={hintId} className="text-xs text-[var(--text-muted)]">
            {hint}
          </p>
        ) : null}

        {/* role="alert" so a validation failure is announced, not just coloured. */}
        {error ? (
          <p id={errorId} role="alert" className="text-xs text-[var(--danger)]">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export function Input({ className, ...props }: React.ComponentPropsWithoutRef<"input">) {
  const { id, describedBy, invalid } = useFieldBinding();
  return (
    <input
      id={id}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className={cn(controlClasses, invalid && "border-[var(--danger)]", className)}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: React.ComponentPropsWithoutRef<"textarea">) {
  const { id, describedBy, invalid } = useFieldBinding();
  return (
    <textarea
      id={id}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className={cn(controlClasses, invalid && "border-[var(--danger)]", className)}
      {...props}
    />
  );
}

/**
 * Stays a native <select> deliberately. Three E2E specs drive course and locale
 * pickers with Playwright's selectOption, which requires a real <select>, and a
 * custom listbox would also mean owning keyboard and screen-reader behaviour
 * that the platform already gets right.
 */
export function Select({ className, ...props }: React.ComponentPropsWithoutRef<"select">) {
  const { id, describedBy, invalid } = useFieldBinding();
  return (
    <select
      id={id}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className={cn(controlClasses, "appearance-none pr-8", className)}
      {...props}
    />
  );
}

export { controlClasses };
